import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AddressCard from '../AddressCard/AddressCard';
import PageLoader from '../PageLoader/PageLoader';
import usePageLoader from '../../hooks/usePageLoader';
import './SavedAddresses.css';
import { normalizePhilippinesAddress } from '../../utils/metroManilaLocations';
import { getErrorMessage } from '../../utils/errorUtils';

/**
 * SavedAddresses component - Manages user's saved shipping addresses.
 * NOTE: No longer renders its own page shell or sidebar — AccountLayout handles that.
 * Features: Add, Edit, Delete addresses, Set default address
 */
function SavedAddresses() {
    const { currentUser, getUserProfile, updateUserProfile } = useAuth();
    const [addresses, setAddresses] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editingData, setEditingData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [actionError, setActionError] = useState(null);

    // ── Page-level overlay: shows immediately on mount, hides once data is ready ──
    const { pageReady, markPageReady } = usePageLoader();

    useEffect(() => {
        if (currentUser) {
            fetchAddresses();
        }
    }, [currentUser]);

    const fetchAddresses = async () => {
        setLoading(true);
        const userData = await getUserProfile(currentUser.uid);
        setAddresses(userData?.savedAddresses || []);
        setLoading(false);
        markPageReady(); // ← signal overlay to hide
    };

    const handleAddNewAddress = () => {
        const newAddress = {
            id: `addr_${Date.now()}`,
            firstName: '',
            lastName: '',
            houseNo: '',
            region: '',
            province: '',
            city: '',
            barangay: '',
            zipCode: '',
            landmark: '',
            isDefault: addresses.length === 0
        };
        setAddresses([newAddress, ...addresses]);
        setEditingId(newAddress.id);
        setEditingData(newAddress);
    };

    const handleEdit = (address) => {
        setEditingId(address.id);
        setEditingData({ ...address });
    };

    const handleCancel = (addressId) => {
        const address = addresses.find(addr => addr.id === addressId);
        if (!address.firstName && !address.lastName && !address.houseNo) {
            setAddresses(addresses.filter(addr => addr.id !== addressId));
        }
        setEditingId(null);
        setEditingData({});
    };

    const handleSave = async (addressId) => {
        setActionError(null);
        if (!editingData.firstName || !editingData.lastName || !editingData.houseNo ||
            !(editingData.region || editingData.province) || !editingData.city || !editingData.barangay || !editingData.zipCode) {
            setActionError('Please fill in all required fields');
            return;
        }
        if (!/^\d{4}$/.test(editingData.zipCode)) {
            setActionError('Please enter a valid 4-digit zip code');
            return;
        }

        setSaving(true);
        try {
            const updatedAddresses = addresses.map(addr =>
                addr.id === addressId ? normalizePhilippinesAddress(editingData) : addr
            );
            await updateUserProfile(currentUser.uid, { savedAddresses: updatedAddresses });
            setAddresses(updatedAddresses);
            setEditingId(null);
            setEditingData({});
            setSuccessMessage('Your address has been saved successfully!');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Error saving address:', error);
            setActionError(getErrorMessage(error, 'Failed to save address. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (addressId) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;

        setSaving(true);
        try {
            const updatedAddresses = addresses.filter(addr => addr.id !== addressId);
            if (updatedAddresses.length > 0) {
                const deletedWasDefault = addresses.find(addr => addr.id === addressId)?.isDefault;
                if (deletedWasDefault) updatedAddresses[0].isDefault = true;
            }
            await updateUserProfile(currentUser.uid, { savedAddresses: updatedAddresses });
            setAddresses(updatedAddresses);
            setSuccessMessage('Your address has been deleted.');
            setShowSuccessDialog(true);
        } catch (error) {
            console.error('Error deleting address:', error);
            setActionError(getErrorMessage(error, 'Failed to delete address. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleSetDefault = async (addressId) => {
        setSaving(true);
        try {
            const updatedAddresses = addresses.map(addr => ({
                ...addr,
                isDefault: addr.id === addressId
            }));
            await updateUserProfile(currentUser.uid, { savedAddresses: updatedAddresses });
            setAddresses(updatedAddresses);
        } catch (error) {
            console.error('Error setting default address:', error);
            setActionError(getErrorMessage(error, 'Failed to set default address. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setEditingData(prev => normalizePhilippinesAddress({ ...prev, [field]: value }));
    };

    return (
    <>
        {showSuccessDialog && (
            <div className="sa-dialog-overlay">
                <div className="sa-dialog">
                    <div className="sa-dialog-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                    </div>
                    <p className="sa-dialog-title">Success</p>
                    <p className="sa-dialog-msg">{successMessage}</p>
                    <button className="sa-dialog-btn" onClick={() => setShowSuccessDialog(false)}>Done</button>
                </div>
            </div>
        )}
        {/* ── Global page overlay — shown until data arrives ── */}
            {!pageReady && <PageLoader message="Loading your addresses…" />}

            {/* ── Per-data spinner (original, kept as-is) ── */}
            {loading ? (
                <div className="saved-addresses-content">
                    <div className="ph-loading">
                        <div className="ph-spinner"/>
                        <span>Loading your addresses…</span>
                    </div>
                </div>
            ) : (
                <div className="saved-addresses-content">
                    {actionError && (
                        <div className="sa-error-banner" onClick={() => setActionError(null)}>
                            {actionError}
                        </div>
                    )}
                    <div>
                        <h1>Saved Addresses</h1>
                        <p className="sa-description">
                            Manage your saved shipping addresses for faster checkout.
                        </p>
                    </div>
                    <hr className="sa-divider" />

                    <div className="addresses-list">
                        {addresses.map((address) => (
                            <AddressCard
                                key={address.id}
                                address={address}
                                isEditing={editingId === address.id}
                                editingData={editingData}
                                onEdit={handleEdit}
                                onSave={handleSave}
                                onCancel={handleCancel}
                                onDelete={handleDelete}
                                onSetDefault={handleSetDefault}
                                onInputChange={handleInputChange}
                                showDelete={addresses.length > 1}
                                saving={saving}
                            />
                        ))}
                    </div>

                    <button
                        className="add-address-button"
                        onClick={handleAddNewAddress}
                        disabled={saving || editingId !== null}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"/>
                            <line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        Add a new Address
                    </button>
                </div>
            )}
        </>
    );
}

export default SavedAddresses;